import { CheckCircleIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import {
  CheckIcon,
  ArrowTopRightOnSquareIcon,
  ArrowLeftEndOnRectangleIcon,
  TrashIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { updateUser, getUserById } from '../../api/user.ts';
import Cookies from 'js-cookie';
import { clearTokens, logoutUser } from '../../api/identity.ts';
import { useNavigate } from 'react-router-dom';
import { onClickUrl } from '../../utils/openinNewTab.tsx';
import { handleEmailClick } from '../../utils/handleEmailClick.tsx';
import {
  getSocials,
  updateBio,
  updateDmInstructions,
  updateProfileDescription,
} from '../../api/social.ts';
import { useSocial } from '../../context/socialProvider.tsx';
import { platformConfig } from '../../components/PlatformIcons.tsx';

const platformOrder = [1, 2, 3, 4, 5]; // Adjust to match your platformConfig keys order

const Settings = () => {
  const [initialBio, setInitialBio] = useState('');
  const [bio, setBio] = useState(initialBio);
  const [initialDmInstructions, setInitialDmInstructions] = useState('');
  const [dmInstructions, setDmInstructions] = useState(initialDmInstructions);
  const [initialProfileDescription, setInitialProfileDescription] =
    useState('');
  const [profileDescription, setProfileDescription] = useState(
    initialProfileDescription
  );
  const [expanded, setExpanded] = useState(false);

  const [initialEmailNotifs, setInitialEmailNotifs] = useState(true);
  const [emailNotifs, setEmailNotifs] = useState(initialEmailNotifs);

  const { currentSocial, availableSocials, switchSocial } = useSocial();

  const toggleSidebar = () => {
    setExpanded(!expanded);
  };

  const [openPlatforms, setOpenPlatforms] = useState<{
    [key: string]: boolean;
  }>({});

  const togglePlatform = (platformName: string) => {
    setOpenPlatforms((prev) => ({
      ...prev,
      [platformName]: !prev[platformName],
    }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const userId = localStorage.getItem('userId'); // Make sure the key you pass here is correct
      if (!userId) return;

      if (currentSocial?.profileDescription) {
        setProfileDescription(currentSocial?.profileDescription);
        setInitialProfileDescription(currentSocial?.profileDescription);
      } else {
        setProfileDescription('');
        setInitialProfileDescription('');
      }

      if (currentSocial?.bio) {
        setBio(currentSocial?.bio);
        setInitialBio(currentSocial?.bio); // Initialize initialBio as well so it matches the fetched bio
      } else {
        setBio('');
        setInitialBio('');
      }

      if (currentSocial?.dmDescription) {
        setDmInstructions(currentSocial?.dmDescription);
        setInitialDmInstructions(currentSocial?.dmDescription);
      } else {
        setDmInstructions('');
        setInitialDmInstructions('');
      }

      console.log('currentSocial', currentSocial);
    };

    if (currentSocial && currentSocial.id) {
      fetchUserData();
    }
  }, [currentSocial]);

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutUser();
      localStorage.clear();
      clearTokens();
      navigate('/login');
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout failed', error);
      localStorage.clear();
      clearTokens();
      navigate('/login');
    }
  };

  const handleSave = async (
    setInitialValue: any,
    value: any,
    field: string
  ) => {
    const userId = localStorage.getItem('userId');

    try {
      if (field === 'bio' && currentSocial != null) {
        const update = updateBio(currentSocial.id, value);
      } else if (field === 'dmInstructions' && currentSocial != null) {
        const update = updateDmInstructions(currentSocial.id, value);
      } else if (field === 'profileDescription' && currentSocial != null) {
        const update = updateProfileDescription(currentSocial.id, value);
      }
    } catch (error: any) {
      console.error(error);
    }

    console.log('saved', field);
    setInitialValue(value);
  };

  // Platforms that are "coming soon"
  const comingSoonPlatforms = [3, 4, 5];

  // Group socials by platform
  const socialsByPlatform = platformOrder.map((platformId) => {
    const platform = platformConfig[platformId];
    const socials = availableSocials.filter((s) => s.provider === platformId);
    return {
      platformId,
      platform,
      socials,
    };
  });

  return (
    <div className="h-screen bg-black">
      <div className="justify-between h-20 mob:h-24 bg-grey flex items-center mob:justify-start border-b-1 border-white border-opacity-20 ">
        <h1 className="mob:ml-4 hover:cursor-default ">Settings</h1>
        <ChevronLeftIcon className="p-2.5 w-8 bg-white bg-opacity-10 rounded-full opacity-0 mr-5" />
      </div>
      <div className="flex flex-col space-y-4 pb-6 mt-4">
        <div className="settings-container">
          <h2 className="font-semibold text-xl mb-4">Connections</h2>
          <span className="pb-6 font-small">
            {' '}
            Not seeing any of your previously connected accounts?{' '}
            <span
              onClick={() => window.location.reload()}
              className="text-yellow hover:cursor-pointer"
            >
              Refresh
            </span>{' '}
            the page
          </span>
          <div className="flex flex-col gap-4">
            {socialsByPlatform.map(({ platform, socials, platformId }) => {
              const isComingSoon = comingSoonPlatforms.includes(platformId);
              const isOpen = openPlatforms[platform.name] || false;
              return (
                <div key={platform.name}>
                  {/* Platform pill */}
                  <button
                    className={`flex items-center w-full px-6 py-3 rounded-full text-lg font-semibold shadow transition-all
                      ${
                        isComingSoon
                          ? 'bg-neutral-800 text-neutral-500 cursor-not-allowed opacity-60'
                          : 'bg-neutral-900 text-white ' +
                            (isOpen
                              ? 'ring-2 ring-violet'
                              : 'hover:bg-neutral-800')
                      }
                    `}
                    onClick={() => {
                      if (!isComingSoon) togglePlatform(platform.name);
                    }}
                    disabled={isComingSoon}
                  >
                    <img
                      src={platform.icon}
                      alt={`${platform.name} logo`}
                      className="w-7 h-7 mr-3"
                    />
                    <span className="flex-1 text-left">{platform.name}</span>
                    {isComingSoon ? (
                      <span className="text-xs bg-neutral-700 text-neutral-300 px-3 py-1 rounded-full ml-2">
                        Coming soon
                      </span>
                    ) : (
                      <ChevronRightIcon
                        className={`w-6 h-6 ml-2 transition-transform ${
                          isOpen ? 'rotate-90' : ''
                        }`}
                      />
                    )}
                  </button>
                  {/* Accounts list */}
                  {!isComingSoon && isOpen && (
                    <div className="mt-2 mb-4 ml-2 flex flex-col gap-2">
                      {socials.map((social) => (
                        <button
                          key={social.id}
                          onClick={() => {
                            switchSocial(social.id);
                            navigate(
                              `/${social.id}/${encodeURIComponent(
                                social.providerName
                              )}/settings`
                            );
                          }}
                          className="flex items-center px-4 py-2 rounded-lg bg-white/5 hover:bg-violet/10 transition-all"
                        >
                          <img
                            src={social.profilePictureUrl}
                            alt={`${social.providerName || 'Account'} profile`}
                            className="w-10 h-10 rounded-full object-cover border border-white/20"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src =
                                'https://superpower-alpha.s3.amazonaws.com/default_images/no_pfp.jpg';
                            }}
                          />
                          <span className="ml-4 font-medium text-white text-base truncate max-w-[160px]">
                            @{social.providerName}
                          </span>
                          <ChevronRightIcon className="w-5 h-5 text-violet ml-auto" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {availableSocials.length === 0 && (
              <div className="text-neutral-400 text-center py-8">
                No connected accounts yet.
              </div>
            )}
          </div>
        </div>

        <div className="settings-container">
          <h2 className="font-semibold mb-4 text-xl">Legal</h2>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={onClickUrl(
                'https://www.superpower.social/privacy-policy'
              )}
              className="flex items-center bg-white text-black rounded-2xl p-2 w-max hover:brightness-90"
            >
              Privacy Policy
              <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={onClickUrl(
                'https://www.superpower.social/terms-of-service'
              )}
              className="flex items-center bg-white text-black rounded-2xl p-2 w-max hover:brightness-90"
            >
              Terms of Service
              <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={handleEmailClick}
              className="flex items-center bg-white text-black rounded-2xl p-2 w-max hover:brightness-90"
            >
              Contact
              <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        <div className="bg-black mx-4 rounded-2xl p-3 p-3 flex flex-col settings-container">
          <button
            className="hover:brightness-75 flex flex-row"
            onClick={handleLogout}
          >
            <ArrowLeftEndOnRectangleIcon className="w-6 h-6 mr-2" />
            Sign Out
          </button>
          <button className="hover:brightness-75" onClick={handleEmailClick}>
            <span
              className="flex flex-row text-red mt-4"
              onClick={handleEmailClick}
            >
              <TrashIcon className="w-6 h-6 mr-2" />
              Request account deletion
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Save = ({
  initialValue,
  currentValue,
  onClick,
}: {
  initialValue: any;
  currentValue: any;
  onClick: any;
}) => {
  const isButtonEnabled = initialValue != currentValue;
  return (
    <button
      className={`flex flex-row justify-center items-center mt-2 text-black rounded-2xl p-2 w-24 self-end 
      ${
        isButtonEnabled
          ? 'bg-yellow text-black hover:brightness-90'
          : 'bg-white bg-opacity-10 text-white'
      }`}
      onClick={onClick}
      disabled={!isButtonEnabled}
    >
      Save
      <CheckIcon className="w-4 h-4 ml-2" />
    </button>
  );
};

export default Settings;
